import React from 'react'
import './index.scss'
import classNames from 'classnames'
import { observer, inject } from 'mobx-react'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { ProgressBar } from '@winnin/winnin_ui.components.progress-bar'
import { Title } from '@winnin/winnin_ui.components.title'

import ColumnGraph from 'components/ColumnGraph'
import Flag from 'components/Flag'
import NoDataAvailable from 'components/NoDataAvailable'
import { RCTooltip } from 'components/RCTooltip'
import { COLORS, AGES } from 'consts'
import { Filters, countries } from 'utils'

import ListMetricVariation from '../ListMetricVariation'

const getLargestGender = genderObject => {
  const _keys = Object.keys(genderObject).filter(k => isAgeKey.test(k))
  return _keys.reduce((acc, k) => {
    const current = genderObject[k]
    if (current > acc) {
      acc = current
    }
    return acc
  }, 0)
}

const getKeyFromObjByValue = (obj, value) => {
  const _keys = Object.keys(obj)
  return _keys.reduce((acc, k) => {
    if (obj[k] === value) {
      acc = k
    }
    return acc
  }, null)
}

const getMainAudience = audience => {
  const male = findGender(audience.ageGender, 'm')
  const female = findGender(audience.ageGender, 'f')

  if (male && female) {
    const largestMale = getLargestGender(male)
    const largestFemale = getLargestGender(female)

    const largestPercentage = largestMale > largestFemale ? largestMale : largestFemale
    const gender = largestMale > largestFemale ? 'male' : 'female'
    const ageKey =
      largestMale > largestFemale
        ? getKeyFromObjByValue(male, largestPercentage)
        : getKeyFromObjByValue(female, largestPercentage)

    //if there is no age key it's because audience isnt populated for that
    if (ageKey) {
      const age = Filters.get('contentAndAudience', 'ages').filter(({ key }) => key === ageKey)[0].label
      return {
        age,
        gender,
      }
    }
  }
  return {}
}

const findGender = (ageGender, gender) =>
  ageGender.reduce((acc, ageGender) => {
    if (ageGender.gender === gender) acc = ageGender
    return acc
  }, [])

const isAgeKey = /a[0-9]+_[0-9]+/

const getGenderPercent = genderObject => {
  const _keys = Object.keys(genderObject).filter(k => isAgeKey.test(k))
  const percent =
    _keys.reduce((acc, k) => {
      const age = genderObject[k]
      return acc + age
    }, 0) * 100
  return percent
}

const getAudiencePercentagesByGender = ({ ageGender }) => {
  const male = findGender(ageGender, 'm')
  const female = findGender(ageGender, 'f')

  if (male && female) {
    const malePercentage = getGenderPercent(male)
    const femalePercentage = getGenderPercent(female)

    return {
      malePercentage,
      femalePercentage,
    }
  }

  return {}
}

const getCountry = countryList => {
  if (countryList && !!countryList.length) {
    const largestCountry = countryList[0]

    const { country, percent: countryPercentage } = largestCountry
    return {
      country,
      countryPercentage,
    }
  }
  return {}
}

const FlagComponent = code => code && <Flag code={code} />

const MiniCardDefault = inject('routingStore')(
  observer(
    ({
      title,
      value,
      explanation,
      subtitle,
      leftSideContent,
      routingStore,
      primaryHeader,
      href,
      className,
      CustomValueComponent,
      contentTooltipMessage,
      sideTooltip = 'right',
      notHasMetric = false,
      tooltipVariationMessage,
      tooltip = false,
      variation = false,
      metricVariation,
      maxViewers = false,
      emptyMetricMessage,
    }) => {
      const containerClass = classNames('mini-card-container', className, { link: !!href }, { hoverEffect: !!href })
      const onClick = href ? () => routingStore.push(href) : null

      if (notHasMetric) {
        return (
          <div className="mini-card-container">
            <div className="mini-card-container-title">
              <Title weight="regular" tag="h5">
                {title}
              </Title>
            </div>

            <Title className="without-metric" weight="regular" tag="h6">
              {emptyMetricMessage}
            </Title>
          </div>
        )
      }

      return (
        <div className={classNames(containerClass)} onClick={onClick}>
          <div className={classNames('content-wrapper', { 'max-viewers': maxViewers })}>
            <div className="mini-card-container-title">
              <Title weight="regular" tag="h5">
                {title}
              </Title>
              {explanation && (
                <RCTooltip tooltipMessage={explanation} direction={sideTooltip || 'right'}>
                  <div className="centered-icon">
                    <Icon width={20} height={20} icon="questionMark" fill={COLORS.primary_light1} />
                  </div>
                </RCTooltip>
              )}
            </div>

            {(value || subtitle) && (
              <>
                <div>
                  {leftSideContent && <div className="left-side">{leftSideContent}</div>}
                  <div className="right-side">
                    <div className="right-side-line">
                      {!CustomValueComponent ? (
                        <Title
                          tag="h2"
                          className={classNames('content', { 'tooltipped tooltipped-ne': contentTooltipMessage })}
                          aria-label={contentTooltipMessage}
                          variation={primaryHeader ? 'primary' : 'default'}
                        >
                          {value}
                        </Title>
                      ) : (
                        <CustomValueComponent />
                      )}

                      {variation && title !== 'Videos' && (
                        <ListMetricVariation
                          metricVariation={metricVariation}
                          tooltipVariationMessage={tooltipVariationMessage}
                          tooltip={tooltip}
                        />
                      )}
                    </div>
                    <Title weight="regular" tag="h6" className={classNames({ 'empty-max-viewers': maxViewers })}>
                      {subtitle}
                    </Title>
                  </div>
                </div>
              </>
            )}
            {!leftSideContent && !value && !subtitle && <NoDataAvailable class small />}
          </div>
        </div>
      )
    }
  )
)

const GenderAudienceValue = (malePercentage, femalePercentage) =>
  malePercentage &&
  femalePercentage && (
    <>
      <div className="gender-audience-percentages">
        <div className="male">
          {`${malePercentage.toFixed(0)}%`}
          <Title weight="regular" tag="h6">
            Male
          </Title>
        </div>
        <div className="female">
          {`${femalePercentage.toFixed(0)}%`}
          <Title weight="regular" tag="h6">
            Female
          </Title>
        </div>
      </div>
      <div className="progress">
        {malePercentage && (
          <ProgressBar
            percentage={malePercentage}
            backgroundColor={COLORS.primary}
            foregroundColor={COLORS.graph_item_3}
          />
        )}
      </div>
    </>
  )

const GenderCard = ({ audience, className, href = '' }) => {
  const { malePercentage, femalePercentage } = getAudiencePercentagesByGender(audience)
  return (
    <MiniCardDefault
      title={'Estimated Gender Audience'}
      explanation="Audience demographics is estimated by our proprietary artificial intelligence tool using public data from people engaging with this content."
      className={className}
      value={GenderAudienceValue(malePercentage, femalePercentage)}
      href={href}
    />
  )
}

const MainAudienceCard = ({ audience, href }) => {
  const { gender, age } = getMainAudience(audience)

  const leftSideContent = gender ? (
    <Icon width={20} height={20} icon={gender} className={`gender-icon ${gender}`} />
  ) : null
  const subtitle = gender ? gender : null
  const value = age ? age : null

  return (
    <MiniCardDefault
      explanation="Audience demographics is estimated by our proprietary artificial intelligence tool using public data from people engaging with this content."
      leftSideContent={leftSideContent}
      sideTooltip="left"
      subtitle={subtitle}
      title={'Estimated Main Audience'}
      value={value}
      href={href}
    />
  )
}

const FlagCard = ({ country, href }) => {
  const result = getCountry(country)
  return (
    <MiniCardDefault
      explanation="Audience demographics is estimated by our proprietary artificial intelligence tool using public data from people engaging with this content."
      title={'Estimated Main Country'}
      value={countries.byKeyValue('ALPHA_2', result.country).ALPHA_3}
      subtitle={countries.byKeyValue('ALPHA_2', result.country).NAME}
      leftSideContent={FlagComponent(result.country)}
      href={href}
    />
  )
}

const getAudienceGraphValuesByGender = genderObject => {
  const _keys = Object.keys(genderObject).filter(k => isAgeKey.test(k))
  const allValues = _keys.map(k => genderObject[k])

  // "55+" is the sum between "55-64" and "65-99"
  const values = [...allValues.slice(0, -2), allValues[5] + allValues[6]]

  return values
}

const getAudienceGraphData = ({ ageGender }) => {
  const male = findGender(ageGender, 'm')
  const female = findGender(ageGender, 'f')

  if (male && female) {
    const maleData = getAudienceGraphValuesByGender(male)
    const femaleData = getAudienceGraphValuesByGender(female)

    return {
      maleData,
      femaleData,
    }
  }

  return {}
}

const getAudienceGraphLabels = () => {
  // only "55+" instead of "55-64" and "65-99"
  const allAgeLabels = AGES.map(v => v.NAME.replace('a', '').replace('_', '-'))
  const ageLabels = [...allAgeLabels.slice(0, -2), '55+']

  return ageLabels
}

const EstimatedAudienceCard = inject('routingStore')(
  observer(({ audience, routingStore, href, className, sideTooltip = 'right' }) => {
    const containerClass = classNames('mini-card-container', className, { link: !!href })
    const onClick = href ? () => routingStore.push(href) : null

    const femaleColor = COLORS.primary
    const maleColor = COLORS.graph_item_3

    const { femalePercentage, malePercentage } = getAudiencePercentagesByGender(audience)
    const { femaleData, maleData } = getAudienceGraphData(audience)
    const ageLabels = getAudienceGraphLabels()

    const graphData = [
      {
        name: 'Female',
        data: femaleData,
      },
      {
        name: 'Male',
        data: maleData,
      },
    ]
    const graphOptions = {
      chart: {
        animations: {
          enabled: false,
        },
        parentHeightOffset: 0,
      },
      plotOptions: {
        bar: {
          distributed: false,
          columnWidth: '40%',
        },
      },
      stroke: {
        show: true,
        width: 4,
        colors: ['transparent'],
      },
      xaxis: {
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        labels: {
          hideOverlappingLabels: false,
          offsetY: -4,
          style: {
            colors: COLORS.gray_dark1,
            fontFamily: 'Muli, Arial, sans-serif',
          },
        },
      },
      yaxis: {
        floating: true,
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        labels: {
          show: false,
        },
        max: max => max,
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
        padding: {
          top: -26,
          right: -18,
          bottom: -18,
          left: -26,
        },
      },
      tooltip: {
        y: {
          formatter: val => (Math.abs(val) * 100).toFixed(1) + '%',
        },
      },
    }

    return (
      <div className={`EstimatedAudienceCard ${containerClass}`} onClick={onClick}>
        <div className="content-wrapper">
          <div className="mini-card-container-title">
            <Title weight="regular" tag="h5">
              Estimated Audience
            </Title>
            <RCTooltip
              tooltipMessage="Audience demographics is estimated by our proprietary artificial intelligence tool using public data from people engaging with this content."
              direction={sideTooltip}
            >
              <div className="centered-icon">
                <Icon width={20} height={20} icon="questionMark" fill={COLORS.primary_light1} />
              </div>
            </RCTooltip>
          </div>

          {audience?.ageGender ? (
            <div className="content">
              <div className="graph">
                <ColumnGraph
                  height="40%"
                  categories={ageLabels}
                  data={graphData}
                  colors={[femaleColor, maleColor]}
                  options={graphOptions}
                />
              </div>

              <div className="progress-bar">
                <div className="labels">
                  <Title weight="regular" tag="h6">
                    <span className="bullet" style={{ background: femaleColor }} /> Female
                  </Title>

                  <Title weight="regular" tag="h6">
                    <span className="bullet" style={{ background: maleColor }} /> Male
                  </Title>
                </div>

                <ProgressBar percentage={femalePercentage} foregroundColor={femaleColor} backgroundColor={maleColor} />

                <div className="percentages">
                  <span>{`${femalePercentage.toFixed(0)}%`}</span>
                  <span>{`${malePercentage.toFixed(0)}%`}</span>
                </div>
              </div>
            </div>
          ) : (
            <NoDataAvailable class small />
          )}
        </div>
      </div>
    )
  })
)

const MiniCards = {
  GenderCard,
  MainAudienceCard,
  FlagCard,
  MiniCardDefault,
  EstimatedAudienceCard,
}

export default MiniCards
